import { rigControlLogger } from "@/utils/loggers";
import { captureException } from "@/utils/sentry";
import { BandHelper, type FlrigResponse } from "@/services/FlrigService";
import type { RigControlService } from "@/services/RigControlService";
import { HAMLIB_MODES } from "@/constants/hamlibModes";
import type { BandType, CommonModes } from "@/types";

export interface HamlibConfig {
  modelId: number;
  modelName: string;
  port: string;
  baud: number;
  customPath?: string;
  /** Fixed TCP port for the internal rigctld; undefined = random free port. */
  rigctldPort?: number;
  /**
   * 'managed' (default) spawns our own rigctld; 'external' connects to one the
   * operator is already running (so the radio can be shared with other apps).
   */
  connectionMode?: 'managed' | 'external';
  /** External-mode rigctld address. */
  networkHost?: string;
  networkPort?: number;
}

export interface HamlibState {
  frequency: number;
  mode?: string;
  power: number;
}

// Modes hamlib uses for digital (FT8/FT4) operation
const HAMLIB_DIGITAL_MODES = ['PKTUSB', 'PKTLSB'];

export function mapHamlibModeToCommonMode(mode: string): string {
  const modeMapping: Record<string, string> = {
    'USB': 'SSB',
    'LSB': 'SSB',
    'CW': 'CW',
    'CWR': 'CW',
    'PKTUSB': 'FT8',
    'PKTLSB': 'FT8',
    'RTTY': 'RTTY',
    'RTTYR': 'RTTY',
    'AM': 'AM',
    'AMS': 'AM',
    'FM': 'FM',
    'FMN': 'FM',
    'PKTFM': 'FM',
    'WFM': 'FM',
  };

  return modeMapping[mode] || mode;
}

export function mapCommonModeToHamlibMode(mode: CommonModes, band?: BandType): string {
  switch (mode) {
    case 'SSB':
      if (!band) {
        throw new Error('Band is required to set SSB mode');
      }
      return BandHelper.getPreferredSsbMode(band);
    case 'CW':
      return 'CW';
    case 'FT8':
    case 'FT4':
    case 'DATA':
      return 'PKTUSB';
    case 'RTTY':
      return 'RTTY';
    case 'AM':
      return 'AM';
    case 'FM':
      return 'FM';
    default:
      return mode;
  }
}

function hamlibBridge() {
  return (window as any).api?.hamlib;
}

const NOT_AVAILABLE: FlrigResponse<never> = {
  success: false,
  error: 'Built-in rig control (Hamlib) is only available in the HAMRS desktop app.',
  errorCode: 'CONNECTION_ERROR',
};

/**
 * Rig control backend that drives the radio directly over a serial port via
 * hamlib's rigctld, managed by the Electron main process.
 */
export class HamlibService implements RigControlService {
  private config: HamlibConfig;
  public availableModes: string[];
  public currentState: HamlibState;

  constructor(config: HamlibConfig) {
    this.config = config;
    this.availableModes = [...HAMLIB_MODES];
    this.currentState = {
      frequency: 0,
      power: 0,
    };
  }

  public static isSupported(): boolean {
    return Boolean(hamlibBridge());
  }

  public mapToCommonMode(mode: string): string {
    return mapHamlibModeToCommonMode(mode);
  }

  /**
   * Runs an IPC call to the main process, converting any rejection (the main
   * process threw, the channel is gone) into a structured failure instead of a
   * thrown error. Rig control must never take down the calling code path.
   */
  private async callBridge<T>(op: string, fn: () => Promise<FlrigResponse<T>>): Promise<FlrigResponse<T>> {
    try {
      return await fn();
    } catch (error) {
      rigControlLogger(`hamlib ${op} failed:`, error);
      captureException(error, { feature: 'rig-control', backend: 'hamlib', op });
      return {
        success: false,
        error: 'Lost communication with the radio control service.',
        errorCode: 'CONNECTION_ERROR',
      };
    }
  }

  private get isExternal(): boolean {
    return this.config.connectionMode === 'external';
  }

  private isConfigured(): boolean {
    if (this.isExternal) {
      return Boolean(this.config.networkHost && this.config.networkPort);
    }
    return Boolean(this.config.modelId && this.config.port && this.config.baud);
  }

  private async ensureStarted(): Promise<FlrigResponse<boolean>> {
    const bridge = hamlibBridge();
    if (!bridge) return NOT_AVAILABLE;

    if (!this.isConfigured()) {
      return {
        success: false,
        error: this.isExternal
          ? 'Enter the host and port of the rigctld you want to connect to.'
          : 'Select a radio model and serial port first (or use Detect My Radio).',
        errorCode: 'MISSING_ARGUMENTS',
      };
    }

    // start() is idempotent when the daemon is already running with this config
    if (this.isExternal) {
      return this.callBridge<boolean>('start', () => bridge.start({
        modelId: 0,
        port: '',
        baud: 0,
        external: { host: this.config.networkHost!, port: this.config.networkPort! },
      }));
    }
    return this.callBridge<boolean>('start', () => bridge.start({
      modelId: this.config.modelId,
      port: this.config.port,
      baud: this.config.baud,
      customPath: this.config.customPath,
      rigctldPort: this.config.rigctldPort,
    }));
  }

  public async testConnection(): Promise<FlrigResponse<boolean>> {
    rigControlLogger(this.isExternal
      ? `Testing hamlib connection to external rigctld at ${this.config.networkHost}:${this.config.networkPort}`
      : `Testing hamlib connection: ${this.config.modelName} on ${this.config.port} @ ${this.config.baud}`);

    const startResponse = await this.ensureStarted();
    if (!startResponse.success) {
      return startResponse;
    }

    // rigctld can start even when the radio is off; confirm it actually answers
    const frequencyResponse = await this.getFrequency();
    if (!frequencyResponse.success) {
      return {
        success: false,
        error: frequencyResponse.error || 'Radio did not respond',
        errorCode: frequencyResponse.errorCode === 'CONNECTION_ERROR' ? 'CONNECTION_ERROR' : 'NO_RADIO',
      };
    }

    return { success: true, value: true };
  }

  public async getFrequency(): Promise<FlrigResponse<number>> {
    const bridge = hamlibBridge();
    if (!bridge) return NOT_AVAILABLE;

    const response = await this.callBridge<number>('getFrequency', () => bridge.getFrequency());
    if (response.success && response.value !== undefined) {
      this.currentState.frequency = response.value;
    }
    return response;
  }

  public async setFrequency(hz: number): Promise<FlrigResponse<void>> {
    const bridge = hamlibBridge();
    if (!bridge) return NOT_AVAILABLE;

    const startResponse = await this.ensureStarted();
    if (!startResponse.success) {
      return startResponse as FlrigResponse<void>;
    }

    const response = await this.callBridge<void>('setFrequency', () => bridge.setFrequency(hz));
    if (response.success) {
      this.currentState.frequency = hz;
    }
    return response;
  }

  public async getMode(): Promise<FlrigResponse<string>> {
    const bridge = hamlibBridge();
    if (!bridge) return NOT_AVAILABLE;

    const response = await this.callBridge<string>('getMode', () => bridge.getMode());
    if (response.success && response.value) {
      this.currentState.mode = response.value;
    }
    return response;
  }

  public async setMode(mode: CommonModes, band?: BandType): Promise<FlrigResponse<string | void>> {
    if (!band && mode === 'SSB') {
      return {
        success: false,
        error: 'Band is required to set mode',
        errorCode: 'MISSING_ARGUMENTS',
      };
    }

    const bridge = hamlibBridge();
    if (!bridge) return NOT_AVAILABLE;

    const startResponse = await this.ensureStarted();
    if (!startResponse.success) {
      return startResponse as FlrigResponse<void>;
    }

    // If we are already on the 'right' mode for FT8, don't change it
    if (['FT8', 'FT4'].includes(mode) && this.currentState.mode && HAMLIB_DIGITAL_MODES.includes(this.currentState.mode)) {
      return { success: true, value: this.currentState.mode };
    }

    const hamlibMode = mapCommonModeToHamlibMode(mode, band);

    // Skip a redundant set-mode when the rig is already in the exact target
    // mode (sideband included, since mapCommonModeToHamlibMode resolves SSB to
    // USB/LSB by band). A click-to-QSY should issue a mode command only when
    // the spot's mode actually differs - sending one needlessly can disturb
    // rig state (e.g. the KX2's CW filter). The main process also passes
    // RIG_PASSBAND_NOCHANGE so even a real mode change leaves the passband be.
    if (this.currentState.mode && this.currentState.mode === hamlibMode) {
      return { success: true, value: hamlibMode };
    }

    const response = await this.callBridge<void>('setMode', () => bridge.setMode(hamlibMode));
    if (response.success) {
      this.currentState.mode = hamlibMode;
      return { success: true, value: hamlibMode };
    }
    return response;
  }

  public async getPower(): Promise<FlrigResponse<number>> {
    const bridge = hamlibBridge();
    if (!bridge) return NOT_AVAILABLE;

    const response = await this.callBridge<number>('getPower', () => bridge.getPower());
    if (response.success && response.value !== undefined) {
      this.currentState.power = response.value;
    }
    return response;
  }

  public async getAvailableModes(): Promise<FlrigResponse<string[]>> {
    // Hamlib normalizes modes across radios, so the vocabulary is static
    return { success: true, value: this.availableModes };
  }

  public async disconnect(): Promise<void> {
    const bridge = hamlibBridge();
    if (!bridge) return;
    try {
      await bridge.stop();
    } catch (error) {
      rigControlLogger('Error stopping hamlib:', error);
    }
  }
}

export default HamlibService;
